import { useFormikContext } from 'formik';
import { Divider, Grid2, Typography } from '@mui/material';
import { CSSProperties, Fragment, useState } from 'react';
import { TimePicker } from '@mui/x-date-pickers-pro';
import { CreateArtistValues } from '../../utils/form/artistForm';
import { OnSchedCommonType } from '../../types/on-sched';
import {
  convertDateToOnschedHours,
  convertOnschedHoursToDate,
  convertShortDayToFull,
} from '../../utils/global/helper';
import { ShortDay } from '../../types/employees';
import IOSSwitch from '../global/IOSSwitch';
import {
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
} from '../../constants/employees';
import useConfirm from '../../hooks/useConfirm';
import {
  getAvailabilityWarning,
  orderAvailabilityListByDay,
} from '../../helpers/employees';
import EditContentModal from '../global/EditContentModal';
import appTheme from '../../constants/theme';

interface Props {
  open: boolean;
  onClose: () => void;
  availability: OnSchedCommonType.Availability;
}

const pickerStyle: CSSProperties = {
  fontSize: appTheme.typography.body2.fontSize,
  height: '40px',
};

function ArtistHoursInputsModal({
  onClose,
  open,
  availability: initialAvailability,
}: Props) {
  const confirm = useConfirm();

  const { values, setFieldValue } = useFormikContext<CreateArtistValues>();

  const [availability, setAvailability] = useState(initialAvailability);

  const onDone = async () => {
    // only show warning for days that have changed
    const changedAvailability = Object.keys(availability).reduce<
      Partial<OnSchedCommonType.Availability>
    >((acc, day) => {
      const currentHours = availability[day as ShortDay];
      const initialHours = initialAvailability[day as ShortDay];
      const isChanged =
        currentHours.startTime !== initialHours.startTime ||
        currentHours.endTime !== initialHours.endTime;

      if (isChanged) {
        acc[day as ShortDay] = availability[day as ShortDay];
      }
      return acc;
    }, {});

    const warning = getAvailabilityWarning(changedAvailability);
    if (warning) {
      const confirmed = await confirm(warning);
      if (!confirmed) return;
    }

    setFieldValue('weeklyAvailability', availability);
    onClose();
  };

  if (!open) return null;

  const onChangeDate = (
    day: ShortDay,
    key: keyof OnSchedCommonType.Time,
    value: Date | null,
  ) => {
    if (!value) return;

    let onschedHours = convertDateToOnschedHours(value);

    if (onschedHours === 0 && key === 'endTime') {
      onschedHours = 2400;
    }

    setAvailability({
      ...availability,
      [day]: {
        ...availability[day],
        [key]: onschedHours,
      },
    });
  };

  const onToggleCloseDay = (day: ShortDay, closed: boolean) => {
    const isInitialOpen =
      initialAvailability[day].startTime !== 0 &&
      initialAvailability[day].endTime !== 0;

    // either revert to initial hours or set to 9-5
    const startTime = closed
      ? 0
      : isInitialOpen
      ? initialAvailability[day].startTime
      : DEFAULT_START_TIME;
    const endTime = closed
      ? 0
      : isInitialOpen
      ? initialAvailability[day].endTime
      : DEFAULT_END_TIME;

    setAvailability({
      ...availability,
      [day]: {
        startTime,
        endTime,
      },
    });
  };

  const isDayClosed = (day: ShortDay) =>
    availability[day].startTime === 0 && availability[day].endTime === 0;

  return (
    <EditContentModal
      open={open}
      onClose={onClose}
      title="Manage Hours"
      onDone={onDone}>
      {orderAvailabilityListByDay(Object.entries(availability)).map(
        ([day, hours]: [string, OnSchedCommonType.Time], index) => {
          const isClosed = isDayClosed(day as ShortDay);
          return (
            <Fragment key={day}>
              <Grid2
                container
                columns={12}
                width="100%"
                justifyContent="space-between"
                alignItems="center"
                gap={{
                  xs: 1,
                  sm: 0,
                }}
                paddingX={2}
                paddingY={2.5}>
                <Grid2
                  size={{
                    xs: 12,
                    sm: 2,
                  }}
                  order={{
                    xs: 2,
                    sm: 1,
                  }}>
                  <IOSSwitch
                    checked={!isClosed}
                    value={!isClosed}
                    onChange={() =>
                      onToggleCloseDay(day as ShortDay, !isClosed)
                    }
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                </Grid2>

                <Grid2
                  size={{
                    xs: 12,
                    sm: 3,
                  }}
                  order={{
                    xs: 1,
                    sm: 2,
                  }}>
                  <Typography variant="body1">
                    {convertShortDayToFull(day as ShortDay)}
                  </Typography>
                </Grid2>

                <Grid2
                  order={3}
                  size={{
                    xs: 12,
                    sm: 7,
                  }}
                  flexWrap="nowrap"
                  display="flex"
                  alignItems="center"
                  justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
                  gap={2}>
                  <TimePicker
                    slotProps={{
                      textField: {
                        InputProps: {
                          style: pickerStyle,
                        },
                        sx: {
                          width: '140px',
                        },
                      },
                    }}
                    timezone={values.timezoneId ?? undefined}
                    value={convertOnschedHoursToDate(hours.startTime)}
                    onAccept={value =>
                      onChangeDate(day as ShortDay, 'startTime', value)
                    }
                    onChange={value =>
                      onChangeDate(day as ShortDay, 'startTime', value)
                    }
                    disabled={isClosed}
                  />
                  <Typography variant="body1">-</Typography>
                  <TimePicker
                    slotProps={{
                      textField: {
                        InputProps: {
                          style: pickerStyle,
                        },
                        sx: {
                          width: '140px',
                        },
                      },
                    }}
                    timezone={values.timezoneId ?? undefined}
                    value={convertOnschedHoursToDate(hours.endTime)}
                    onAccept={value =>
                      onChangeDate(day as ShortDay, 'endTime', value)
                    }
                    onChange={value =>
                      onChangeDate(day as ShortDay, 'endTime', value)
                    }
                    disabled={isClosed}
                  />
                </Grid2>
              </Grid2>
              {index < Object.keys(availability).length - 1 && (
                <Divider
                  color="#F2F2F2"
                  variant="fullWidth"
                  component="div"
                  sx={{
                    borderColor: '#F2F2F2',
                  }}
                />
              )}
            </Fragment>
          );
        },
      )}
    </EditContentModal>
  );
}

export default ArtistHoursInputsModal;
